// Command modules are imported LAZILY (dynamic `import()` inside each switch
// case below) so `--help` / `--version` and the top-level HELP_TEXT pay ZERO of
// the import-and-evaluate cost of the heavy command modules (Projects /
// Collections / Broker / Agents / Context internals + their api-client/topic
// graph). esbuild bundles to a single CJS file with no code-splitting, so a
// dynamic import compiles to a deferred require: the module's top-level code runs
// only when its command is actually invoked. api-client is light and used on
// every path (profileMark at the top of main, error reporting at exit), so it
// stays a STATIC import — only the ./commands/* modules became dynamic.
import { analyticsCommandError, flushAndExit } from './analytics'
import { getCachedWorkspace, parseErrorMeta, profileMark } from './api-client'
import { nudgeStaleVersions } from './version-check'
// Command HELP text is the ONE thing index.ts needs eagerly (to print `<cmd>
// --help` without dispatching into the command). It lives in a side-effect-free
// help-text module — pure template strings, ZERO heavy deps — so importing it
// here does NOT pull in the command bodies (api-client / context graph). Each
// command module re-exports its own const from the same place (single source),
// which is what the help-consistency spec pins.
import {
  NOTE_HELP, AREA_HELP, AGENT_HELP, MEMBER_HELP, REPO_HELP,
  AUDIT_HELP, BROKER_HELP, EXPORT_HELP, TAGS_HELP, INTEGRATION_HELP, WORK_HELP,
} from './commands/help-text'

declare const __VERSION__: string

const VERSION = __VERSION__

const HELP_TEXT = `Driftless CLI v${VERSION} — the shared context layer for humans and coding agents

Docs:  https://docs.driftless.icu     Run 'driftless <command> --help' for any command below.

Driftless is organized around what you're trying to DO. Pick the workflow:

─ First time here ────────────────────────────────────────────────────────────
  driftless login --key <api-key>          Connect this machine to your workspace
  driftless install-skill                  Teach your agent the workflow (writes CLAUDE.md / AGENTS.md)
  driftless doctor                         Verify auth, API, git and repo link are healthy

─ Before you touch code: pull the team's context ──────────────────────────────
  driftless context get <topic>            Read one topic before editing the area it governs
  driftless context get --files "a,b"      What does the team know about THESE files?
  driftless context get --diff             Same, for the files you're changing right now
  driftless context search "<query>"       Don't know the topic name? Search by keyword
  driftless context list                   Browse what's documented (drifted topics first)

─ After you learn something: write it down ────────────────────────────────────
  driftless context add "<name>" --what "..." --pattern "src/x/**"   Document a new area
  driftless context update <topic> --gotcha "..." --decision "..."   Add to an existing one
  driftless note add --content "..."       Just jot it — a private note you can refine later

─ Keep the vault clean ────────────────────────────────────────────────────────
  driftless context doctor                 Audit the layer (stale, unassigned, duplicates, …)
  driftless context merge <src> --into <dst>   Fold a duplicate into its survivor (--dry-run first)

─ Run a project loop (the agent's board) ──────────────────────────────────────
  driftless project list                   See the boards
  driftless project card next <id>         Get the next card + its context bundle (one loop tick)
  driftless project card status <id> <card> done   Mark a card done, then loop again

─ Coordinate live sessions (Active Work) ──────────────────────────────────────
  driftless work start --project <id> --card <id> --objective "..."   Preflight + register this session
  driftless work active                    Who else is working this repo right now?
  driftless work finish --outcome success  Close the session (card moves only if --card-status)

─ Inspect a collection (CRMs, trackers, pipelines) ────────────────────────────
  driftless collection list                List the configured tables
  driftless collection get <id>            See one collection's schema, views and lifecycle
  driftless collection records <id>        Read its rows

─ Connect an integration (SETUP — external systems) ───────────────────────────
  driftless integration catalog            Which providers can I connect?
  driftless integration connect <provider> Start the connect flow (then confirm)
  driftless integration list               What's connected to this workspace

─ Operate an integration (EXECUTION — governed) ───────────────────────────────
  driftless broker connections             Which providers are connected
  driftless broker operations <provider>   What can I call on this connection?
  driftless broker invoke <provider> <op>  Run an operation (inline + audited)

─ Refresh drift (what stopped being true) ─────────────────────────────────────
  driftless sync                           Pull the drift signal for this repo (stale topics, team PRs)
  driftless context get --diff --mark      Flag the topics your local changes drifted (no GitHub App needed)
  driftless context doctor                 Audit the vault: stale / orphaned / zombie / draft / repo-leak

─ Workspace admin & housekeeping ──────────────────────────────────────────────
  area              Areas — the domains topics file under (list·add·rename·rm)
  tags              Tag registry — cross-cutting labels (list·add·rename·rm)
  agent             The headless workers (Auditor, Architect, Librarian) — runs·config·usage·stats
  repo              Linked GitHub repos (list·link·unlink)
  branches          Which branches' pushes count as drift
  member            Members + roles — owner/admin (list·invite·role·rm·invites)
  audit             Workspace audit log — owner/admin
  export            Export the full workspace bundle as JSON — owner/admin
  workspace         Switch between the workspaces you belong to (list·switch·current·add)
  hooks             Experimental auto-pull-context hook for Claude Code (enable·disable)

Context subcommands:
  list [flags]                  List context topics
                                  flags: --stale --docs --manual --auto --suggested --repo <id> --tag <name> --limit <n> --all
  get <topic>                   Live view of one topic
  get --files "p1,p2"           Match topics by file paths
  get --diff [--mark]           Match topics for local changes (--mark persists drift, no GitHub App)
  search "<query>" [--tag <t>]  Full-text search across topics (--tag scopes to a category)
  add "<name>" --what "..."     Create a new topic (patterns validated against
                                  local checkout at write time)
  update <topic> --gotcha "..." Append a gotcha (--decision/--invariant/--check too)
  update <topic> --what "..."   Replace structured fields
  update <topic> --add-pattern  Add anchor pattern (idempotent, validated)
  update <topic> --remove-pattern  Remove anchor pattern (idempotent)
  link <topic>                  Register current repo into topic's where_repos
  relations <topic>             List typed relations (incoming/outgoing)
  graph <topic> [--depth N]     Render local relation graph
  doctor                        Audit context (stale/orphaned/zombie/draft/docs/repo-leak)
  coverage                      Uncovered source areas (where the team has no topic)
  events [--limit n] [--type t] Recent topic events (FILE_CHANGED / UPDATED / ...)
  merge <src> --into <dst>      Fold a duplicate into its survivor (archives source)
  move <topic> --to <ws>        Re-home a topic to another workspace you belong to
  delete <topic>                Delete a topic

Flags:
  --json        Output as JSON (default is human-readable)
  --dry-run     Preview changes without writing to Driftless Cloud
  --status <reviewed|draft>     Set topic status on create / update

Auth priority:
  1. DRIFTLESS_API_KEY env var
  2. ~/.driftless/config.json

Environment:
  DRIFTLESS_API_KEY    API key for authentication
  DRIFTLESS_API_URL    API server URL (default: https://api.driftless.icu/api/v1)

Examples:
  driftless sync                          # what drifted around my topics?
  driftless context get sdk               # read team context before editing
  driftless context search "auth"
  driftless context get --files "src/auth/guard.ts"

  driftless context add "b2b-guard" \\
    --what "Guard protecting B2B endpoints" \\
    --how "Verifies org_id from the auth token" \\
    --pattern "src/shared/guards/**"

  driftless context update b2b-guard \\
    --gotcha "Token refresh races during 401 retry — wrap in mutex" \\
    --decision "Chose jose over jsonwebtoken for ESM compat" \\
    --add-pattern "src/shared/auth/**"
`

function showCommandHelp(cmd: string) {
  const help: Record<string, string> = {
    login: `driftless login --key <api-key>

Authenticate with Driftless Cloud.
Creates ~/.driftless/config.json with your API key and API URL.

Options:
  --key <key>       API key (required)
  --url <url>       API URL (default: https://api.driftless.icu/api/v1)

Example:
  driftless login --key drift_xxx
`,
    context:`driftless context <subcommand> [args] [flags]

The team's shared codebase memory. Topics are the durable WHY (what / how /
gotchas / decisions / invariants) anchored to file globs. The two moves are:
READ what the team knows before you touch an area, WRITE one clean note after
you learn. Human-readable by default; use --json for machines.

Read — before you work:
  get <topic>                   Read one topic (the full body) before editing its area
  get --files "p1,p2,..."       Match topics for given file paths
  get --diff [--mark]           Match topics for the files you're changing (--mark flags drift, no GitHub App)
  search "<query>" [--tag <t>] [--limit n]  Find topics by keyword (--tag scopes a category; --limit caps results, max 50)
  list [filters]                Browse topics (drifted first; see List filters below)

Write — after you learn:
  add "<name>" [opts]           Create a new topic (patterns validated at write time)
  update <topic> [opts]         Add gotchas/decisions/invariants, or replace a field
  sync <topic> --note "..."     Attach a quick note to a topic
  sync <topic> --doc <path>     Attach a doc to a topic
  delete <topic>                Delete a topic
  merge <src> --into <dst>      Fold a duplicate into its survivor (archives source)
  move <topic> --to <ws>        Re-home a topic to another workspace (re-review there)

Explore the graph:
  relations <topic>             List typed relations (incoming/outgoing)
  graph <topic> [--depth N]     Render the local relation graph
  link <topic>                  Register the current repo into this topic's where_repos

Keep it healthy:
  doctor                        Audit the vault (stale / orphaned / zombie / draft / docs-pending / repo-leak)
  coverage                      Source areas with no topic yet

Share:
  share <topic> [--revoke]      Public read-only link to a topic
  share <topic> --with a,b      Share a note with teammates (emails or user ids)
  shared [--read <id> | --all]  Notes shared WITH you (mark one / all read)

Governance — trust model: Note → Knowledge (a note becomes Knowledge once it's merged in)
(you write notes and propose; merging is an owner/admin act you run only when explicitly asked. The agent treats Knowledge as truth):
  propose <topic>               Put a note up for review
  approve <topic>               Merge a note into the team's Knowledge (owner/admin only; run it only when asked)
  reject <topic>                Send a note back, with feedback
  archive <topic>               Retire a topic
  pr <topic>                    List suggested edits (content changes to existing Knowledge)
  pr <topic> --open --summary "..." <content flags>   Open a suggested edit
  pr <topic> --merge <id>       Merge a suggested edit into Knowledge (human)
  pr <topic> --reject <id>      Close a suggested edit (human)

List filters:
  --stale                       Only stale topics
  --docs                        Only topics with anchored docs
  --manual                      Only manually-created topics
  --auto                        Only auto-created topics (scoped to the current repo)
  --suggested                   Only not-yet-Knowledge topics (Notes + notes up for review)
  --repo <id>                   Only topics scoped to a given repo
  --limit <n>                   Cap the list to N rows (default 40; --json is uncapped)
  --all                         Show every topic (no cap)

Search options:
  --tag <name>                  Scope the search to a category (repeatable; OR)
  --limit <n>                   Cap results server-side (server hard-caps at 50; default 50)
  --include-notes               Also search your own private notes (drafts)

Topic fields (add / update) — REPLACE vs APPEND matters:
  --what "..."                  What it is                      (replace)
  --how "..."                   How it works                    (replace)
  --decisions "..."             Why it's built this way         (replace — overwrites)
  --ownership "..."             Who owns it                     (replace)
  --content "<text>"            Markdown body                   (replace)
  --content @path/to/file.md    Read content from file          (replace)
  --tags tag1,tag2              Comma-separated labels          (replace)
  --area <name|id>              File topic under an Area        (auto-creates a new name)
  --rel <type>:<slug>           Typed relation to another topic (repeatable)
  --pattern "src/x/**"          Anchor glob, replaces all       (repeatable)
  --add-pattern "src/x/**"      Add one anchor                  (repeatable, idempotent)
  --remove-pattern "src/x/**"   Remove one anchor               (repeatable, idempotent)
  --where "src/x.ts"            Explicit file anchor            (replace)
  --decision "..."              Append ONE decision             (repeatable, accumulates)
  --gotcha "..."                Append ONE gotcha               (repeatable, accumulates)
  --invariant "..."             Append ONE invariant            (repeatable, accumulates)
  --check "..."                 Append ONE required check       (repeatable, accumulates)
  --invariants "..."            Replace ALL invariants[]        (repeatable = whole array)
  --checks "..."                Replace ALL required_checks[]   (repeatable = whole array)
  Note: --decisions REPLACES the whole field; --decision APPENDS one entry
  (same for --gotcha vs --gotchas, --invariant vs --invariants, --check vs
  --checks). Pick by intent: APPEND to add a new
  standalone fact (atomic, safe under concurrency). REWRITE to correct or
  consolidate — context get -> integrate -> --content/--decisions. Replace
  is destructive: pull fresh and fold in the old, never blind-overwrite.

Pattern validation at write time:
  Every --pattern / --add-pattern is globbed against the local checkout
  before the API call. The CLI reports per pattern:
    ✓ matches N files — sample paths
    ⚠ over-broad (> 100 files) — split the topic
    ⚠ matches only node_modules/dist/.git — likely unintended
    ✗ 0 matches — BLOCKING (use --where for an explicit path instead)

Flags:
  --json                        Machine-readable JSON output
  --dry-run                     Preview changes without writing
  --status <reviewed|draft>     Set topic status on create / update

Examples:
  driftless context list
  driftless context get auth-boundaries
  driftless context get --diff
  driftless context get --files "src/auth/guard.ts"
  driftless context search "auth"
  driftless context add "sdk" --pattern "src/sdk/**" --what "Public SDK"
  driftless context update sdk \\
    --decision "Chose fetch over axios to drop a dep" \\
    --gotcha "Breaks on Node < 18" \\
    --add-pattern "src/sdk/v2/**"
  driftless context doctor
  driftless context link shared-auth     # repo this checkout points at
`,
    note: NOTE_HELP,
    work: WORK_HELP,
    area: AREA_HELP,
    agent: AGENT_HELP,
    member: MEMBER_HELP,
    repo: REPO_HELP,
    audit: AUDIT_HELP,
    broker: BROKER_HELP,
    integration: INTEGRATION_HELP,
    export: EXPORT_HELP,
    tags: TAGS_HELP,
    sync: `driftless sync [--json]

Pull drift signal for the current repo: stale topics, team PR activity,
and suggested topics pending review.

Options:
  --json       Machine-readable output

Example:
  driftless sync
`,
    'install-skill': `driftless install-skill

Install the agent skill into the current repository. Writes:
  - CLAUDE.md          @-reference to .driftless/skill.md (auto-loaded)
  - AGENTS.md          managed Driftless block (re-fetched every run)
  - .driftless/        skill body, references/, assets/templates/

Once installed, AI agents (Claude Code, Codex, etc.) follow the skill
to retrieve context before coding (driftless context get / search) and to
persist discoveries afterwards (driftless context update / add).

Re-run safely — the AGENTS.md block is self-healing and references are
re-fetched on every invocation, so stale instructions get refreshed.

Example:
  cd my-repo
  driftless install-skill
`,
    doctor: `driftless doctor

Check environment health before using Driftless.
Verifies:
  1. API key present (env var or config file)
  2. API server reachable
  3. Git remote configured
  4. Workspace resolved via /me
  5. Repo linked to workspace
  6. AGENTS.md with Driftless skill
  7. GitHub App installed (if applicable)

Exits 1 if any check fails.

Example:
  driftless doctor
`,
    branches: `driftless branches [add|rm] <branch>

View or set which branches' pushes count as drift. The repo's default
branch (from GitHub) is ALWAYS tracked automatically — zero config. Add
extra branches only if your real integration branch isn't the GitHub
default (e.g. you ship from 'release' or 'develop').

Subcommands:
  (none)            Show the tracked branches
  add <branch>      Also detect drift from pushes to <branch>
  rm <branch>       Stop detecting drift from <branch>

Flags:
  --json       Machine-readable output

Examples:
  driftless branches
  driftless branches add release
  driftless branches rm staging --json
`,
    hooks: `driftless hooks <enable|disable>

EXPERIMENTAL — deterministic auto-pull context for Claude Code.

Instead of relying on the skill (the agent *should* pull context), this
registers a Claude Code PreToolUse hook so that BEFORE every edit, the team's
AUTHORITATIVE System context for that file is injected automatically — the
agent codes with the gotchas/invariants/decisions without anyone running a
command. Cost-safe: only authoritative System topics, deduped per session, size-
capped, and a no-op when nothing matches.

Subcommands:
  enable        Add the hook to .claude/settings.json (in the current repo)
  disable       Remove it

How it works: 'enable' writes a PreToolUse hook calling 'driftless hook
pre-edit'; Claude Code runs it before Edit/Write and injects the result.

Examples:
  driftless hooks enable
  driftless hooks disable
`,
  }

  if (help[cmd]) {
    console.log(help[cmd])
  } else {
    console.log(`No help available for "${cmd}". Run "driftless --help" for all commands.`)
  }
}

async function main() {
  const args = process.argv.slice(2)
  const command = args[0]

  // Profiling (F0.2): mark startup (node boot + module import elapsed before
  // dispatch). HTTP stages are captured per call in the api-client; the total
  // and the summary are emitted to stderr at process exit.
  profileMark('startup', process.uptime() * 1000)

  // Stale-version nudge: tell the user when their CLI or installed skill is
  // behind the latest. Only for real working commands (not help/version/login),
  // skipped under --json, and run before dispatch because most commands exit
  // internally. Best-effort and cached daily, so it never blocks the command.
  const NUDGE_COMMANDS = new Set(['context', 'note', 'project', 'work', 'collection', 'area', 'agent', 'member', 'repo', 'audit', 'broker', 'integration', 'export', 'sync', 'install-skill', 'doctor', 'branches'])
  if (NUDGE_COMMANDS.has(command) && args[1] !== '--help') {
    await nudgeStaleVersions(args.includes('--json'))
  }

  // Each command module is imported ONLY when its command runs (dynamic
  // `import('./commands/x')`). esbuild's single-file CJS bundle turns this into a
  // deferred require, so the heavy modules below (and their api-client/context
  // graph) are never evaluated for `--help`, `--version`, or a sibling command.
  // The `<cmd> --help` branches print from the eagerly-loaded help-text module
  // and never reach into the command body.
  switch (command) {
    case 'context':
      if (args[1] === '--help') { showCommandHelp('context') } else { await (await import('./commands/context')).contextCommand(args.slice(1)) }
      break
    case 'note':
      if (args[1] === '--help') { showCommandHelp('note') } else { await (await import('./commands/note')).noteCommand(args.slice(1)) }
      break
    case 'project':
    case 'projects':
      await (await import('./commands/project')).projectCommand(args.slice(1))
      break
    case 'work':
      if (args[1] === '--help') { showCommandHelp('work') } else { await (await import('./commands/work')).workCommand(args.slice(1)) }
      break
    case 'collection':
    case 'collections':
      await (await import('./commands/collection')).collectionCommand(args.slice(1))
      break
    case 'area':
    case 'areas':
      if (args[1] === '--help') { showCommandHelp('area') } else { await (await import('./commands/area')).areaCommand(args.slice(1)) }
      break
    case 'agent':
    case 'agents':
      if (args[1] === '--help') { showCommandHelp('agent') } else { await (await import('./commands/agent')).agentCommand(args.slice(1)) }
      break
    case 'member':
    case 'members':
      if (args[1] === '--help') { showCommandHelp('member') } else { await (await import('./commands/member')).memberCommand(args.slice(1)) }
      break
    case 'repo':
    case 'repos':
      if (args[1] === '--help') { showCommandHelp('repo') } else { await (await import('./commands/repo')).repoCommand(args.slice(1)) }
      break
    case 'audit':
      if (args[1] === '--help') { showCommandHelp('audit') } else { await (await import('./commands/audit')).auditCommand(args.slice(1)) }
      break
    case 'broker':
      if (args[1] === '--help') { showCommandHelp('broker') } else { await (await import('./commands/broker')).brokerCommand(args.slice(1)) }
      break
    case 'integration':
    case 'integrations':
      if (args[1] === '--help') { showCommandHelp('integration') } else { await (await import('./commands/integration')).integrationCommand(args.slice(1)) }
      break
    case 'export':
      if (args[1] === '--help') { showCommandHelp('export') } else { await (await import('./commands/export')).exportCommand(args.slice(1)) }
      break
    case 'sync':
      if (args[1] === '--help') { showCommandHelp('sync') } else { await (await import('./commands/sync')).syncCommand(args.slice(1)) }
      break
    case 'install-skill':
      if (args[1] === '--help') { showCommandHelp('install-skill') } else { await (await import('./commands/install-skill')).installSkillCommand() }
      break
    case 'login':
      if (args[1] === '--help') { showCommandHelp('login') } else { await (await import('./commands/login')).loginCommand(args.slice(1)) }
      break
    case 'doctor':
      if (args[1] === '--help') { showCommandHelp('doctor') } else { await (await import('./commands/doctor')).doctorCommand(args.slice(1)) }
      break
    case 'branches':
      if (args[1] === '--help') { showCommandHelp('branches') } else { await (await import('./commands/branches')).branchesCommand(args.slice(1)) }
      break
    case 'tags':
      await (await import('./commands/tags')).tagsCommand(args.slice(1))
      break
    case 'workspace':
    case 'workspaces':
      // `workspace --help` / no subcommand both fall through to the usage banner.
      await (await import('./commands/workspace')).workspaceCommand(args.slice(1))
      break
    case 'hook':
      // Called by Claude Code's PreToolUse hook — must stay silent + exit 0.
      await (await import('./commands/hook')).hookCommand(args.slice(1))
      break
    case 'hooks':
      if (args[1] === '--help') { showCommandHelp('hooks') } else { await (await import('./commands/hook')).hooksCommand(args.slice(1)) }
      break
    case 'help':
      if (args[1]) {
        showCommandHelp(args[1])
      } else {
        console.log(HELP_TEXT)
      }
      break
    case undefined:
    case '--help':
    case '-h':
      console.log(HELP_TEXT)
      break
    case '--version':
    case '-v':
      console.log(`driftless v${VERSION}`)
      break
    default:
      console.error(`Unknown command: ${command}`)
      console.error('Run driftless --help for usage.')
      process.exit(1)
  }
}

main().catch((err) => {
  const msg = err?.message ?? String(err)
  console.error('Error:', msg)
  // Point unhandled failures at the one command that diagnoses environment,
  // auth and workspace issues, instead of leaving the user stuck.
  if (/Connection failed|timed out|ENOTFOUND|ECONNREFUSED|Unauthorized|HTTP 401|HTTP 403|resolve workspace/i.test(msg)) {
    console.error('\nNext: run `driftless doctor` to diagnose auth/connectivity/workspace.')
  }
  const command = process.argv.slice(2)[0] ?? 'unknown'
  const slug = getCachedWorkspace().slug
  const meta = parseErrorMeta(err)
  analyticsCommandError(command, slug, msg, {
    error_code: meta.code,
    status_code: meta.statusCode,
    endpoint: meta.endpoint,
    retryable: meta.retryable,
    request_id: meta.request_id,
  }).finally(() => flushAndExit(1))
})
